import Quill, { type Delta, type EmitterSource, type QuillOptions, type Op } from 'quill';
import 'quill/dist/quill.snow.css';
import { formats as quillFormats } from './quillFormatsList';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import RolloverModal from './RolloverModal';

const quillOptions: Omit<QuillOptions, 'modules'> = {
	theme: 'snow',
	placeholder: 'Write something here',
	formats: quillFormats
};

export interface QuillWrapperProps {
	defaultValue?: Delta | Op[];
	toolbarId: string;
	readOnly?: boolean;
	onChange?: (
		value: string,
		deltaOrRange: Delta | Range,
		source: EmitterSource,
		editor: Quill
	) => void;
}

export const QuillWrapper = forwardRef<Quill, QuillWrapperProps>((props, ref) => {
	const { toolbarId, readOnly, defaultValue, onChange } = props;
	const quillRef = useRef<Quill | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const defaultValueRef = useRef<QuillWrapperProps['defaultValue']>(defaultValue);
	const onChangeRef = useRef<QuillWrapperProps['onChange']>(onChange);
	const [selectedText, setSelectedText] = useState('');
	const [attributes, setAttributes] = useState(null);
	const [selectionIndex, setSelectionIndex] = useState(null);
	const [selectionLength, setSelectionLength] = useState(null);
	const [isRolloverModalShown, setRolloverModalShown] = useState(false);

	const handleRolloverModalSubmit = useCallback(
		(payload) => {
			const { term, definition, capitalizeFirstWord, termInGlossary, alphaTermInGlossary } =
				payload;

			const deletion = quillRef.current.editor.deleteText(selectionIndex, selectionLength);
			const insertion = quillRef.current.editor.insertText(selectionIndex, term, {
				rolloverText: {
					term,
					definition,
					capitalizeFirstWord,
					termInGlossary,
					alphaTermInGlossary
				}
			});
			const delta = deletion.concat(insertion);
			// have to manually fire onChange since quillRef.current.update() doesn't seem to do it
			onChangeRef.current?.(quillRef.current.root.innerHTML, delta, 'user', quillRef.current);
		},
		[selectionIndex, selectionLength]
	);

	const handleRolloverModalDelete = useCallback(() => {
		const delta = quillRef.current.editor.removeFormat(selectionIndex, selectionLength);
		onChangeRef.current?.(quillRef.current.root.innerHTML, delta, 'user', quillRef.current);
	}, [selectionIndex, selectionLength]);

	const handleRolloverChange = useCallback(() => {
		const selection = quillRef.current.getSelection();
		const format = quillRef.current.getFormat(selection.index, selection.length);

		if (Object.keys(format).includes('rolloverText')) {
			const [leaf, offset] = quillRef.current.getLeaf(selection.index);

			setAttributes(format['rolloverText']);
			setSelectionIndex(selection.index - offset);
			// @ts-expect-error typing is incorrect, accessing leaf.text is valid
			setSelectionLength(leaf.text.length);
			// @ts-expect-error typing is incorrect, accessing leaf.text is valid
			setSelectedText(leaf.text);
		} else {
			setAttributes(null);
			setSelectionIndex(selection.index);
			setSelectionLength(selection.length);
			setSelectedText(quillRef.current.getText(selection.index, selection.length));
		}

		setRolloverModalShown(true);
	}, []);

	const handleEyebrowHeadingChange = useCallback(() => {
		const selection = quillRef.current.getSelection();
		if (!selection) return;

		const formats = quillRef.current.getFormat(selection);
		const delta = quillRef.current.formatLine(
			selection.index,
			selection.length,
			'eyebrowHeading',
			!formats.eyebrowHeading
		);
		onChangeRef.current?.(quillRef.current.root.innerHTML, delta, 'user', quillRef.current);
	}, []);

	const handleShiftEnter = useCallback((range) => {
		quillRef.current.editor.insertEmbed(range.index, 'break', true);
		quillRef.current.setSelection(range.index + 1, 'silent'); // Move the cursor forward
	}, []);

	useEffect(() => {
		const container = containerRef.current;
		const modules = {
			clipboard: {},
			keyboard: {
				bindings: {
					linebreak: {
						key: 13,
						shiftKey: true,
						handler: handleShiftEnter
					}
				}
			},
			toolbar: {
				container: '#toolbar' + toolbarId,
				handlers: {
					rolloverText: handleRolloverChange,
					eyebrowHeading: handleEyebrowHeadingChange
				}
			}
		};
		const quill = new Quill(container, { ...quillOptions, modules });

		quillRef.current = quill;

		if (defaultValueRef.current) {
			quill.setContents(defaultValueRef.current);
		}

		quill.on(Quill.events.EDITOR_CHANGE, (eventType, delta, _oldDelta, source) => {
			if (eventType === Quill.events.TEXT_CHANGE) {
				onChangeRef.current?.(quillRef.current.root.innerHTML, delta, source, quillRef.current);
			}
		});

		return () => {
			quillRef.current = null;
			container.innerHTML = '';
		};
	}, [handleEyebrowHeadingChange, handleRolloverChange, handleShiftEnter, ref, toolbarId]);

	useEffect(() => {
		if (typeof ref === 'function') {
			ref(quillRef.current);
		} else if (ref) {
			ref.current = quillRef.current;
		}
	}, [ref]);

	useEffect(() => {
		onChangeRef.current = onChange;
	}, [onChange]);

	useEffect(() => {
		quillRef.current?.enable(!readOnly);
	}, [readOnly]);

	return (
		<>
			<div ref={containerRef} />

			{isRolloverModalShown && (
				<RolloverModal
					onSubmit={handleRolloverModalSubmit}
					onDelete={handleRolloverModalDelete}
					onHide={() => setRolloverModalShown(false)}
					isShown={isRolloverModalShown}
					selectedText={selectedText}
					attributes={attributes}
				/>
			)}
		</>
	);
});

QuillWrapper.displayName = 'QuillWrapper';
